import type { Breadcrumb, Event } from '@sentry/react'

const EMAIL_PATTERN = /[\w.+-]+@[\w-]+\.[\w.-]+/g
const BEARER_PATTERN = /Bearer\s+[\w-]+\.[\w-]+\.[\w-]+/gi
const MAX_EXTRA_LENGTH = 2000

// An email inside an encoded path or query reaches us as name%40host, which the pattern
// below would not match.
const decodeSafely = (text: string): string => {
  if (!text.includes('%')) return text

  try {
    return decodeURIComponent(text)
  } catch {
    return text
  }
}

export const scrubText = (text: string): string =>
  decodeSafely(text).replace(EMAIL_PATTERN, '[email]').replace(BEARER_PATTERN, 'Bearer [token]')

// A fragment carries data the same way a query does, and neither helps triage.
const scrubUrl = (url: string): string => scrubText(url.split(/[?#]/)[0])

// Callers attach whole request urls to `extra`, and the search terms live in the query.
const URL_WITH_QUERY = /^(https?:\/\/|\/)\S*\?/

/**
 * Sentry stores our events in its US region, so nothing that identifies a person
 * and no request or response payload may leave the browser. Titles are the worst
 * offender: an unscrubbed "email_not_verified: name@customer.com" ends up as the
 * issue name, indexed and searchable.
 */
const scrubExtraValue = (value: unknown): unknown => {
  if (typeof value === 'string') {
    const scrubbed = URL_WITH_QUERY.test(value) ? scrubUrl(value) : scrubText(value)
    return scrubbed.length > MAX_EXTRA_LENGTH ? `${scrubbed.slice(0, MAX_EXTRA_LENGTH)}… [truncated]` : scrubbed
  }

  if (Array.isArray(value)) {
    return `[array of ${value.length}]`
  }

  if (value && typeof value === 'object') {
    return `[object with keys: ${Object.keys(value).join(', ')}]`
  }

  return value
}

// A React component stack is the whole value of a boundary report and routinely runs
// past the truncation limit, so it is the one extra that is kept whole.
const scrubExtra = (extra: Event['extra']): Event['extra'] => {
  if (!extra) return extra

  return Object.fromEntries(
    Object.entries(extra).map(([key, value]) => [key, key === 'componentStack' ? value : scrubExtraValue(value)]),
  )
}

const scrubTags = (tags: Event['tags']): Event['tags'] => {
  if (!tags) return tags

  return Object.fromEntries(
    Object.entries(tags).map(([key, value]) => [key, typeof value === 'string' ? scrubText(value) : value]),
  )
}

export const scrubEvent = <T extends Event>(event: T): T =>
  ({
    ...event,
    tags: scrubTags(event.tags),
    message: event.message ? scrubText(event.message) : event.message,
    exception: event.exception && {
      ...event.exception,
      values: event.exception.values?.map(value => ({
        ...value,
        value: value.value ? scrubText(value.value) : value.value,
      })),
    },
    request: event.request && {
      ...event.request,
      url: event.request.url ? scrubUrl(event.request.url) : event.request.url,
      query_string: undefined,
      cookies: undefined,
      headers: undefined,
    },
    extra: scrubExtra(event.extra),
  }) as T

// Fetch and xhr breadcrumbs put the url in `url`; navigation breadcrumbs use `from` and `to`.
const URL_DATA_KEYS = ['url', 'from', 'to']

const scrubBreadcrumbData = (data: Breadcrumb['data']): Breadcrumb['data'] => {
  if (!data) return data

  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => [
      key,
      URL_DATA_KEYS.includes(key) && typeof value === 'string' ? scrubUrl(value) : value,
    ]),
  )
}

// The SDK records a fetch span's url verbatim, and endpoints such as potentialDuplicates
// put a company name and address in the query string.
const SPAN_URL_KEYS = ['http.url', 'url.full']
const SPAN_QUERY_KEYS = ['http.query', 'url.query', 'http.fragment', 'url.fragment']

const scrubSpanData = (data: Record<string, unknown> | undefined): Record<string, unknown> | undefined => {
  if (!data) return data

  return Object.fromEntries(
    Object.entries(data).flatMap(([key, value]) => {
      if (SPAN_QUERY_KEYS.includes(key)) return []
      if (SPAN_URL_KEYS.includes(key) && typeof value === 'string') return [[key, scrubUrl(value)]]
      return [[key, value]]
    }),
  )
}

/**
 * beforeSend only sees error events. Transactions are a separate pipeline, and with tracing
 * sampled they carry one span per request with the url the browser actually called.
 */
export const scrubTransaction = <T extends Event>(event: T): T =>
  ({
    ...event,
    request: event.request && {
      ...event.request,
      url: event.request.url ? scrubUrl(event.request.url) : event.request.url,
      query_string: undefined,
      cookies: undefined,
      headers: undefined,
    },
    spans: event.spans?.map(span => ({ ...span, data: scrubSpanData(span.data) })),
  }) as T

/**
 * Console breadcrumbs carry whatever the app logged, which routinely includes
 * response payloads. Everything else is kept for triage context.
 */
export const scrubBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb | null => {
  if (breadcrumb.category === 'console') {
    return null
  }

  return {
    ...breadcrumb,
    data: scrubBreadcrumbData(breadcrumb.data),
    message: breadcrumb.message ? scrubText(breadcrumb.message) : breadcrumb.message,
  }
}
